import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CalendarDays, Loader2, Save, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TeacherShell } from "@/components/TeacherShell";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function SchoolCalendar() {
  const navigate = useNavigate();
  const [periods, setPeriods] = useState([]);
  const [schoolYear, setSchoolYear] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState({ academies: [], years: [] });
  const [academie, setAcademie] = useState("");
  const [year, setYear] = useState("");
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [cal, opt] = await Promise.all([
          api.get("/calendar"),
          api.get("/calendar/options"),
        ]);
        setPeriods(cal.data.periods);
        setSchoolYear(cal.data.school_year);
        setOptions(opt.data);
        setAcademie(opt.data.academies[0] || "");
        setYear(opt.data.years[0] || "");
      } catch {
        toast.error("Chargement du calendrier impossible.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const importOfficial = async () => {
    if (!academie || !year) return;
    setImporting(true);
    try {
      const { data } = await api.get("/calendar/import", { params: { year, academie } });
      setPeriods(data.periods.map((p) => ({ index: p.index, label: p.label, start: p.start, end: p.end })));
      toast.success("Calendrier importé. Vérifiez les dates puis enregistrez.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setImporting(false);
    }
  };

  const setField = (index, field, value) =>
    setPeriods((prev) => prev.map((p) => (p.index === index ? { ...p, [field]: value } : p)));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/calendar", {
        periods: periods.map((p) => ({ index: p.index, start: p.start || "", end: p.end || "" })),
      });
      setPeriods(data.periods);
      toast.success("Calendrier enregistré.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <TeacherShell active="calendrier">
      <div className="mx-auto max-w-3xl">
        <h1 className="flex items-center gap-2 font-heading text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
          <CalendarDays size={22} className="text-blue-700 dark:text-blue-400" /> Calendrier scolaire
        </h1>
        <p className="mb-6 mt-2 text-sm text-slate-500 dark:text-slate-400">
          Définissez les dates des 5 périodes d'évaluation{schoolYear ? ` — année ${schoolYear}` : ""}. Chaque
          semaine de Passeport sera automatiquement rattachée à sa période pour les statistiques.
        </p>

        {loading ? (
          <div className="flex justify-center py-20" data-testid="calendar-loading">
            <Loader2 className="animate-spin text-slate-400" size={26} />
          </div>
        ) : (
          <>
            <div className="mb-5 rounded-2xl border border-slate-100 bg-white p-4 shadow-[0_4px_20px_rgb(0,0,0,0.03)] dark:border-white/10 dark:bg-white/[0.03]" data-testid="import-calendar-card">
              <p className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
                <Download size={16} className="text-slate-400" /> Importer le calendrier scolaire officiel
              </p>
              <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
                Remplit automatiquement les 5 périodes à partir des vacances officielles de votre académie. Les dates restent modifiables.
              </p>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                <div className="flex flex-1 flex-col gap-1.5">
                  <Label htmlFor="academie">Académie</Label>
                  <select
                    id="academie" value={academie} onChange={(e) => setAcademie(e.target.value)}
                    className="h-10 rounded-md border border-input bg-transparent px-3 text-sm"
                    data-testid="import-academie-select"
                  >
                    {options.academies.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
                <div className="flex flex-1 flex-col gap-1.5">
                  <Label htmlFor="year">Année scolaire</Label>
                  <select
                    id="year" value={year} onChange={(e) => setYear(e.target.value)}
                    className="h-10 rounded-md border border-input bg-transparent px-3 text-sm"
                    data-testid="import-year-select"
                  >
                    {options.years.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <Button onClick={importOfficial} disabled={importing} variant="outline" className="rounded-xl" data-testid="import-calendar-button">
                  {importing ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Download size={16} className="mr-2" />}
                  Importer
                </Button>
              </div>
            </div>

            <div className="flex flex-col gap-3" data-testid="periods-list">
              {periods.map((p, i) => (
                <motion.div
                  key={p.index}
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.04 }}
                  className="rounded-2xl border border-slate-100 bg-white p-4 shadow-[0_4px_20px_rgb(0,0,0,0.03)] dark:border-white/10 dark:bg-white/[0.03]"
                  data-testid={`period-row-${p.index}`}
                >
                  <p className="mb-3 text-sm font-semibold text-slate-800 dark:text-slate-100">{p.label}</p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor={`start-${p.index}`}>Date de début</Label>
                      <Input
                        id={`start-${p.index}`} type="date" value={p.start || ""}
                        onChange={(e) => setField(p.index, "start", e.target.value)}
                        data-testid={`period-start-${p.index}`}
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor={`end-${p.index}`}>Date de fin</Label>
                      <Input
                        id={`end-${p.index}`} type="date" value={p.end || ""}
                        onChange={(e) => setField(p.index, "end", e.target.value)}
                        data-testid={`period-end-${p.index}`}
                      />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="mt-6 flex justify-end">
              <Button onClick={save} disabled={saving} className="rounded-xl" data-testid="save-calendar-button">
                {saving ? <Loader2 size={18} className="mr-2 animate-spin" /> : <Save size={17} className="mr-2" />}
                Enregistrer le calendrier
              </Button>
            </div>
          </>
        )}
      </div>
    </TeacherShell>
  );
}
